
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Shield, Globe, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Create accordion components for the content sections
const WhyAccordionItem = ({ 
  title, 
  children, 
  icon, 
  isOpen, 
  toggleOpen 
}: { 
  title: string; 
  children: React.ReactNode; 
  icon: React.ReactNode;
  isOpen: boolean;
  toggleOpen: () => void;
}) => {
  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <button 
        className={`w-full flex items-center justify-between p-5 text-left ${isOpen ? 'bg-frikopplad-cream' : 'bg-white'}`}
        onClick={toggleOpen}
      >
        <div className="flex items-center">
          <div className="mr-3 text-frikopplad-burgundy">
            {icon}
          </div>
          <h3 className="text-xl font-bold text-frikopplad-navy">{title}</h3>
        </div>
        <span className="text-2xl">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      
      {isOpen && (
        <div className="p-5 bg-white border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
};

const WhySwitch = () => {
  // Use the whySwitch namespace for translations
  const { t } = useTranslation('whySwitch');
  const [openSection, setOpenSection] = useState<string | null>("intro");
  
  // Simplified - no need for manual re-renders, useTranslation handles it
  const toggleSection = (section: string) => {
    if (openSection === section) {
      setOpenSection(null);
    } else {
      setOpenSection(section);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-frikopplad-cream to-white">
          <div className="container-custom py-16">
            <h1 className="text-4xl md:text-5xl font-bold text-frikopplad-navy mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-frikopplad-navy opacity-90 max-w-4xl">
              {t('hero.description')}
            </p>
            <div className="mt-8 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <p className="text-center text-gray-700">
                {t('hero.quote')}
              </p>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="container-custom py-12">
          <div className="mx-auto max-w-4xl">
            <WhyAccordionItem 
              title={t('sections.political.title')}
              icon={<Globe className="h-6 w-6" />}
              isOpen={openSection === "intro"}
              toggleOpen={() => toggleSection("intro")}
            >
              <div className="prose max-w-none">
                <p>{t('sections.political.p1')}</p>
                <p className="mt-4">{t('sections.political.p2')}</p>
                <p className="mt-4">{t('sections.political.p3')}</p>
                <p className="mt-4">{t('sections.political.p4')}</p>
              </div>
            </WhyAccordionItem>
            
            <WhyAccordionItem 
              title={t('sections.moderation.title')}
              icon={<Shield className="h-6 w-6" />}
              isOpen={openSection === "content"}
              toggleOpen={() => toggleSection("content")}
            >
              <div className="prose max-w-none">
                <p>{t('sections.moderation.p1')}</p>
                <p className="mt-4">{t('sections.moderation.p2')}</p>
                <p className="mt-4">{t('sections.moderation.p3')}</p>
                <p className="mt-4">{t('sections.moderation.p4')}</p>
                <p className="mt-4">{t('sections.moderation.p5')}</p>
              </div>
            </WhyAccordionItem>
            
            <WhyAccordionItem 
              title={t('sections.compliance.title')}
              icon={<ShieldCheck className="h-6 w-6" />}
              isOpen={openSection === "compliance"}
              toggleOpen={() => toggleSection("compliance")}
            >
              <div className="prose max-w-none">
                <p>{t('sections.compliance.p1')}</p>
                <p className="mt-4">{t('sections.compliance.p2')}</p>
                <p className="mt-4">{t('sections.compliance.p3')}</p>
                <p className="mt-4">{t('sections.compliance.p4')}</p>
                <p className="mt-4">{t('sections.compliance.p5')}</p>
              </div>
            </WhyAccordionItem>
            
            <WhyAccordionItem 
              title={t('sections.cloudAct.title')}
              icon={<Globe className="h-6 w-6" />}
              isOpen={openSection === "cloudact"}
              toggleOpen={() => toggleSection("cloudact")}
            >
              <div className="prose max-w-none">
                <p>{t('sections.cloudAct.p1')}</p>
                <p className="mt-4">{t('sections.cloudAct.p2')}</p>
                <p className="mt-4">{t('sections.cloudAct.p3')}</p>
                <p className="mt-4">{t('sections.cloudAct.p4')}</p>
                <p className="mt-4">{t('sections.cloudAct.p5')}</p>
              </div>
            </WhyAccordionItem>
            
            <WhyAccordionItem 
              title={t('sections.privacy.title')}
              icon={<ShieldCheck className="h-6 w-6" />}
              isOpen={openSection === "privacy"}
              toggleOpen={() => toggleSection("privacy")}
            >
              <div className="prose max-w-none">
                <p>{t('sections.privacy.p1')}</p>
                <p className="mt-4">{t('sections.privacy.p2')}</p>
                <p className="mt-4">{t('sections.privacy.p3')}</p>
                <p className="mt-4">{t('sections.privacy.p4')}</p>
                <p className="mt-4">{t('sections.privacy.p5')}</p>
              </div>
            </WhyAccordionItem>
            
            <WhyAccordionItem 
              title={t('sections.openSource.title')}
              icon={<Shield className="h-6 w-6" />}
              isOpen={openSection === "opensource"}
              toggleOpen={() => toggleSection("opensource")}
            >
              <div className="prose max-w-none">
                <p>{t('sections.openSource.p1')}</p>
                <ul className="list-disc pl-5 mt-4">
                  <li className="mb-2">
                    <strong>{t('sections.openSource.benefits.transparency.title')}:</strong> {t('sections.openSource.benefits.transparency.text')}
                  </li>
                  <li className="mb-2">
                    <strong>{t('sections.openSource.benefits.control.title')}:</strong> {t('sections.openSource.benefits.control.text')}
                  </li>
                  <li className="mb-2">
                    <strong>{t('sections.openSource.benefits.security.title')}:</strong> {t('sections.openSource.benefits.security.text')}
                  </li>
                  <li className="mb-2">
                    <strong>{t('sections.openSource.benefits.adaptability.title')}:</strong> {t('sections.openSource.benefits.adaptability.text')}
                  </li>
                </ul>
                <p className="mt-4">{t('sections.openSource.p2')}</p>
                <p className="mt-4">{t('sections.openSource.p3')}</p>
              </div>
            </WhyAccordionItem>
          </div>
          
          {/* Call to Action */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-frikopplad-cream p-8 rounded-xl shadow-sm">
              <h2 className="text-2xl font-bold text-frikopplad-navy mb-4">{t('cta.title')}</h2>
              <p className="text-lg text-frikopplad-navy/90 mb-6">
                {t('cta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-frikopplad-blue hover:bg-frikopplad-navy" asChild>
                  <Link to="/paket">
                    {t('cta.primaryButton')} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/alternativ">
                    {t('cta.secondaryButton')}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WhySwitch;
